import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { Eye, EyeOff, CheckCircle, Dumbbell, CreditCard } from 'lucide-react';
import { isValidEmail } from '../utils/validation';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

const getStrength = (pw) => {
  if (!pw) return null;
  const strong = pw.length >= 12 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);
  const medium = pw.length >= 8 && (/[A-Z]/.test(pw) || /[0-9]/.test(pw));
  if (strong) return 'strong';
  if (medium) return 'medium';
  return 'weak';
};

const strengthConfig = {
  weak:   { color: 'text-red-500',   bar: 'bg-red-500',   label: 'Weak',   bars: 1 },
  medium: { color: 'text-amber-500', bar: 'bg-amber-500', label: 'Medium', bars: 2 },
  strong: { color: 'text-green-500', bar: 'bg-green-500', label: 'Strong', bars: 3 },
};

const Signup = () => {
  usePageTitle('Create account — Castle Atlas');
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const [form, setForm]               = useState({ fullName: '', email: '', password: '', confirm: '' });
  const [showPw, setShowPw]           = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  /* ── Atlas Plates shell: theme detection (same pattern as Dashboard/Settings) ── */
  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.fullName || !form.email || !form.password) {
      setError('Please fill in all fields.');
      return;
    }
    if (!isValidEmail(form.email)) {
      setError('Please enter a valid professional email address.');
      return;
    }
    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await signUp({
        email: form.email,
        password: form.password,
        fullName: form.fullName,
      });

      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const strength = getStrength(form.password);

  /* ── Atlas Plates tokens ── */
  const pageBg = isDark ? '#0e0d0c' : '#faf9f7';
  const dashColor = isDark ? '#57534e' : '#d4cdc7';
  const mutedColor = isDark ? '#78716c' : '#a8a29e';

  return (
    <div id="main-content" tabIndex={-1} style={{
      minHeight: '100vh', position: 'relative',
      backgroundColor: pageBg,
      transition: 'background-color 0.3s ease',
    }}>
      {/* Contour header — same treatment as Dashboard / Settings */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 180, overflow: 'hidden', pointerEvents: 'none' }}>
        <ContourBG isDark={isDark} size="compact" opacity={0.9} />
      </div>

      <div className="min-h-screen flex flex-col items-center justify-center p-4 font-sans" style={{ position: 'relative', zIndex: 1 }}>

        {/* Plate eyebrow */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>THE GATE</span>
          <span style={{ width: 22, height: 1, background: dashColor, display: 'inline-block' }} />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: mutedColor, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Create account</span>
        </div>

        <div className="w-full max-w-4xl flex flex-col md:flex-row bg-white dark:bg-stone-900 rounded-3xl shadow-xl overflow-hidden border border-stone-200 dark:border-stone-800">

          {/* Left Side: Value Prop (Desktop) */}
          <div className="hidden md:flex md:w-5/12 bg-stone-50 dark:bg-stone-800/50 p-10 flex-col justify-between border-r border-stone-100 dark:border-stone-800">
            <div>
              <div className="flex items-center gap-2.5 mb-8">
                <img src="/logo.svg" alt="Castle Atlas" className="w-8 h-8 dark:brightness-0 dark:invert" />
                <span className="text-xl font-bold tracking-tight text-stone-900 dark:text-white">Castle Atlas</span>
              </div>

              <h2 className="text-3xl font-bold leading-tight text-stone-900 dark:text-white mb-6" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>
                Master in-demand <span className="text-brand-600">skills</span> through real practice.
              </h2>

              <div className="space-y-5">
                {[
                  { icon: CheckCircle, text: 'Phase 1 free — forever',     color: 'text-green-500' },
                  { icon: Dumbbell,    text: '15 scored reps per skill',    color: 'text-orange-500' },
                  { icon: CheckCircle, text: 'Instant AI feedback (0–100)', color: 'text-blue-500' },
                  { icon: CreditCard,  text: 'No credit card required',     color: 'text-stone-400' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-stone-600 dark:text-stone-400">
                    <item.icon className={`w-5 h-5 shrink-0 ${item.color}`} strokeWidth={2.5} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-white dark:bg-stone-800 rounded-2xl border border-stone-100 dark:border-stone-700 shadow-sm">
              <p className="text-xs text-stone-500 italic">"Skills are only built through reps — not videos, not theory. Castle Atlas gives you the reps."</p>
            </div>
          </div>

          {/* Right Side: Form */}
          <div className="flex-1 p-8 md:p-12">
            <div className="max-w-sm mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-stone-900 dark:text-white mb-2" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.02em' }}>Create your account</h1>
                <p className="text-sm text-stone-500 dark:text-stone-400">Join 2,400+ professionals leveling up.</p>
              </div>

              {error && (
                <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-xs font-medium animate-shake" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="signup-name" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Full Name</label>
                  <input id="signup-name" type="text" name="fullName" autoComplete="name" value={form.fullName} onChange={handleChange} placeholder="Jessica Doe" className="input" />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="signup-email" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input id="signup-email" type="email" name="email" autoComplete="email" value={form.email} onChange={handleChange} placeholder="jessica@example.com" className="input" />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="signup-password" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Password</label>
                  <div className="relative">
                    <input id="signup-password" type={showPw ? 'text' : 'password'} name="password" autoComplete="new-password" value={form.password} onChange={handleChange} placeholder="••••••••" className="input pr-10" />
                    <button type="button" onClick={() => setShowPw(p => !p)} aria-label={showPw ? 'Hide password' : 'Show password'}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                        !form.password ? 'text-stone-400' :
                        strength === 'strong' ? 'text-green-500' :
                        strength === 'medium' ? 'text-amber-500' : 'text-red-500'
                      }`}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="signup-confirm" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest ml-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      id="signup-confirm"
                      type={showConfirm ? 'text' : 'password'}
                      name="confirm"
                      autoComplete="new-password"
                      value={form.confirm}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(p => !p)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                        !form.confirm ? 'text-stone-400' :
                        form.password === form.confirm ? 'text-green-500' : 'text-red-500'
                      }`}
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                  {loading
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 21a8 8 0 0 1 13.292-6"/><circle cx="10" cy="8" r="5"/><path d="M19 16v6"/><path d="M22 19h-6"/>
                        </svg>
                        Create account
                      </>
                  }
                </button>
              </form>
            </div>

            <p className="text-center text-sm text-stone-500 dark:text-stone-400 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
